"use client";

import Typography from "@portal/components/atoms/v2/typography";
import Icon from "@portal/components/atoms/icons";
import { FC, useState } from "react";
import { IInfoBoxProps } from "./types";

export interface InfoBoxProps {
  children: React.ReactNode;
  warning?: boolean;
}

interface InfoHeaderBoxProps {
  children: React.ReactNode;
  onClick: () => void;
}

const InfoHeaderBox: FC<InfoHeaderBoxProps> = ({ children, onClick }) => (
  <button onClick={onClick} className={`flex h-6 text-blueLight hover:cursor-pointer hover:underline`}>
    {children}
  </button>
);

const InfoBox: FC<InfoBoxProps> = ({ children, warning = false }) => (
  <div
    className={`flex flex-col justify-center min-h-11 w-full h-full border-l-4 ${
      warning ? "border-l-red" : "border-l-greenDark dark:border-l-gray-600"
    } py-1 px-4 my-1 bg-greyBoxBackground dark:bg-gray-700 overflow-y-auto shadow-md dark:shadow-gray-600`}
  >
    {children}
  </div>
);

/**
 * Component for a box used to display extra information.
 * It can display a clickable header and a text box, or just a text box.
 * @param interface
 * @returns a clickable text and icon, and a text box displayed if expanded.
 */
export const InfoBoxComponent = ({
  header,
  texts,
  marginTop = 0,
  marginBottom = 0,
  warning = false,
  children,
}: IInfoBoxProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const iconName = isExpanded ? "collapseSmallIcon" : "expandSmallRightIcon";

  const renderInfoBox = () => (
    <InfoBox warning={warning}>
      {texts
        ? texts.map((text, i) => (
            <div key={i}>
              <Typography>{text}</Typography>
            </div>
          ))
        : children}
    </InfoBox>
  );

  if (!header) {
    return renderInfoBox();
  }

  return (
    <div className="flex flex-col" style={{ marginTop, marginBottom }}>
      <InfoHeaderBox onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center">
          <span className="text-blueLight">
            <Icon name={iconName} size="xxsmall" />
          </span>
          <Typography>{header}</Typography>
        </div>
      </InfoHeaderBox>
      {isExpanded && renderInfoBox()}
    </div>
  );
};
